/**
 * นำเข้าตารางเวร (วันหยุดหมุนเวียน) — หัวหน้าวางแผนใน Excel แล้วนำเข้าให้ไว
 *   1. เลือกเดือน → 2. วาง (paste) จาก Excel หรือ อัปไฟล์ (.xlsx/.csv)
 *   3. ตรวจก่อนยืนยัน (Preview): จับคู่พนักงาน + แปลรหัส + เตือนรหัสที่อ่านไม่ออก
 *   4. แปลรหัสที่ไม่รู้จัก (ตั้งครั้งเดียว ใช้ซ้ำทุกเดือน) → 5. ยืนยันนำเข้า
 * Round-trip: "ดึงตารางเดือนก่อน" + "ส่งออกเป็น Excel" เพื่อแก้แล้วนำเข้ากลับ
 * ดู docs/SHIFT_ROSTER_BLUEPRINT.md
 */
import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { UploadCloud, FileSpreadsheet, Download, CopyPlus, CheckCircle2, AlertTriangle, CalendarDays } from 'lucide-react';
import { AdminLayout } from '../../components/layout/AdminLayout';
import {
  useRosterImportPreview, useRosterImportCommit, useCodeAlias, useSaveCodeAlias,
  useCopyPrevMonth, useShiftMaster, useOrgDepartments, type RosterImportResult, type RosterImportPayload,
} from '../../hooks';
import { useToast } from '../../components/ui/toast';
import { apiDownload } from '../../lib/api-client';

const OFF_TOKEN = '__OFF__';
const IGNORE_TOKEN = '__IGNORE__';

/** เดือนถัดไป (YYYY-MM) — ค่าเริ่มต้น เพราะงานจัดเวรคือ "วางแผนเดือนหน้า" */
function nextMonth(): string {
  const d = new Date();
  const n = new Date(d.getFullYear(), d.getMonth() + 1, 1);
  return `${n.getFullYear()}-${String(n.getMonth() + 1).padStart(2, '0')}`;
}

const TH_MONTHS = ['มกราคม', 'กุมภาพันธ์', 'มีนาคม', 'เมษายน', 'พฤษภาคม', 'มิถุนายน', 'กรกฎาคม', 'สิงหาคม', 'กันยายน', 'ตุลาคม', 'พฤศจิกายน', 'ธันวาคม'];
/** "2026-07" → "กรกฎาคม 2569" (โชว์ให้ผู้ใช้เห็นชัดว่าลงเดือนไหน) */
function thaiMonthLabel(period: string): string {
  const [y, m] = period.split('-').map(Number);
  if (!y || !m || m < 1 || m > 12) return period;
  return `${TH_MONTHS[m - 1]} ${y + 543}`;
}

/** ดึงเดือนจากชื่อไฟล์ เช่น roster-2026-07.csv → "2026-07" (กันตั้งเดือนผิด) */
function monthFromFileName(name: string): string | null {
  const m = name.match(/(20\d{2})[-_]?(0[1-9]|1[0-2])(?!\d)/);
  return m ? `${m[1]}-${m[2]}` : null;
}

/** parse 1 บรรทัด CSV/TSV → ช่อง (รองรับ tab, และ comma แบบมี quote) */
function splitLine(line: string): string[] {
  if (line.includes('\t')) return line.split('\t');
  const out: string[] = [];
  let cur = '', q = false;
  for (let i = 0; i < line.length; i++) {
    const c = line[i];
    if (q) {
      if (c === '"' && line[i + 1] === '"') { cur += '"'; i++; }
      else if (c === '"') q = false;
      else cur += c;
    } else if (c === '"') q = true;
    else if (c === ',') { out.push(cur); cur = ''; }
    else cur += c;
  }
  out.push(cur);
  return out;
}

function parseText(text: string): string[][] {
  return text.replace(/\r/g, '').split('\n').filter((l) => l.trim() !== '').map(splitLine);
}

/** matrix (คน×วัน) → payload rows: หาแถวหัว=เลขวันที่, คอลัมน์นำ=ชื่อ/รหัส */
function deriveRows(matrix: string[][]): { rows: RosterImportPayload['rows']; dayCount: number; error?: string } {
  if (matrix.length < 2) return { rows: [], dayCount: 0, error: 'ต้องมีอย่างน้อยแถวหัว (เลขวันที่) + 1 แถวข้อมูล' };
  const header = matrix[0]!;
  const dayCols: Array<{ col: number; day: number }> = [];
  let firstDayCol = -1;
  header.forEach((h, col) => {
    const m = String(h ?? '').match(/\d+/);
    const day = m ? Number(m[0]) : NaN;
    if (Number.isInteger(day) && day >= 1 && day <= 31) {
      dayCols.push({ col, day });
      if (firstDayCol === -1) firstDayCol = col;
    }
  });
  if (dayCols.length === 0) return { rows: [], dayCount: 0, error: 'ไม่พบคอลัมน์วันที่ — แถวแรกต้องเป็นเลขวันที่ (1, 2, 3, …)' };
  const idCols = header.map((_, col) => col).filter((col) => col < firstDayCol);
  if (idCols.length === 0) return { rows: [], dayCount: 0, error: 'ไม่พบคอลัมน์ชื่อ/รหัสพนักงาน (ต้องอยู่ก่อนคอลัมน์วันที่)' };

  const rows: RosterImportPayload['rows'] = [];
  for (let r = 1; r < matrix.length; r++) {
    const row = matrix[r]!;
    const key = idCols.map((c) => String(row[c] ?? '').trim()).find((v) => v !== '') ?? '';
    if (!key) continue;
    const cells: Record<string, string> = {};
    for (const { col, day } of dayCols) {
      const v = String(row[col] ?? '').trim();
      if (v !== '') cells[String(day)] = v;
    }
    rows.push({ key, cells });
  }
  return { rows, dayCount: dayCols.length };
}

export function RosterImport() {
  const [period, setPeriod] = useState(nextMonth());
  const [dept, setDept] = useState('');
  const { data: orgDepts } = useOrgDepartments();
  const [text, setText] = useState('');
  const [fileName, setFileName] = useState('');
  const [preview, setPreview] = useState<RosterImportResult | null>(null);
  const [aliasDraft, setAliasDraft] = useState<Record<string, string>>({});

  const toast = useToast();
  const shifts = useShiftMaster();
  const codeAlias = useCodeAlias();
  const previewM = useRosterImportPreview();
  const commitM = useRosterImportCommit();
  const saveAlias = useSaveCodeAlias();
  const copyPrev = useCopyPrevMonth();

  const parsed = useMemo(() => deriveRows(parseText(text)), [text]);

  function payload(): RosterImportPayload { return { period, rows: parsed.rows }; }

  async function onFile(file: File) {
    setFileName(file.name);
    // เดาเดือนจากชื่อไฟล์ (เช่น roster-2026-07) → กันตั้งเดือนผิด
    const fm = monthFromFileName(file.name);
    if (fm && fm !== period) { setPeriod(fm); setPreview(null); }
    if (/\.(xlsx|xls)$/i.test(file.name)) {
      try {
        const XLSX = await import('xlsx');
        const buf = await file.arrayBuffer();
        const wb = XLSX.read(buf, { type: 'array' });
        const ws = wb.Sheets[wb.SheetNames[0]!]!;
        const aoa = XLSX.utils.sheet_to_json<string[]>(ws, { header: 1, raw: false, defval: '' });
        setText(aoa.map((r) => (r ?? []).join('\t')).join('\n'));
      } catch {
        toast.error('อ่านไฟล์ Excel ไม่สำเร็จ — ลองบันทึกเป็น .csv แล้วอัปใหม่');
      }
    } else {
      setText(await file.text());
    }
  }

  function runPreview() {
    if (parsed.error) return toast.error(parsed.error);
    if (parsed.rows.length === 0) return toast.error('ไม่พบข้อมูลในตาราง');
    previewM.mutate(payload(), {
      onSuccess: (r) => { setPreview(r); setAliasDraft({}); },
      onError: (e) => toast.error(e instanceof Error ? e.message : 'ตรวจไม่สำเร็จ'),
    });
  }

  function saveAliasesAndRepreview() {
    const merged = { ...(codeAlias.data?.aliases ?? {}) };
    let n = 0;
    for (const [raw, val] of Object.entries(aliasDraft)) {
      if (val) { merged[raw] = val; n++; }
    }
    if (n === 0) return toast.error('ยังไม่ได้เลือกการแปลรหัส');
    saveAlias.mutate(merged, {
      onSuccess: () => { toast.success('บันทึกการแปลรหัสแล้ว'); runPreview(); },
      onError: (e) => toast.error(e instanceof Error ? e.message : 'บันทึกไม่สำเร็จ'),
    });
  }

  function runCommit() {
    previewM.reset();
    commitM.mutate(payload(), {
      onSuccess: (r) => {
        const rm = r.diagnostics?.roster_mode_set ?? 0;
        toast.success(`นำเข้าตารางเวรเดือน ${thaiMonthLabel(period)} แล้ว ${r.written} ช่อง${rm ? ` · ตั้งโหมดตารางเวรให้ ${rm} คน` : ''}`);
        setPreview(null); setText(''); setFileName('');
      },
      onError: (e) => toast.error(e instanceof Error ? e.message : 'นำเข้าไม่สำเร็จ'),
    });
  }

  const d = preview?.diagnostics;
  const shiftOpts = (shifts.data ?? []).filter((s) => s.active);

  return (
    <AdminLayout title="นำเข้าตารางเวร">
      <div className="max-w-3xl mx-auto space-y-4 pb-12">
        <p className="text-sm text-gray-500 px-1">
          วางแผนกะใน Excel ได้เลย แล้วนำเข้าทีเดียว — ตารางแบบ <b>ชื่อ/รหัสพนักงาน × วันที่</b>
          (แถวแรกเป็นเลขวันที่ 1–31, ช่องเขียน “เช้า/บ่าย/ดึก/หยุด” ตามจริง).
          ระบบจะตรวจและให้ดูก่อนยืนยันเสมอ. พนักงานที่จะใช้ตารางนี้คิดเงิน ต้องตั้งโหมดเป็น “ตารางเวร” ในหน้าพนักงาน.
        </p>

        {/* เดือน + ปุ่ม round-trip */}
        <div className="card p-4 space-y-3">
          {/* แบนเนอร์เดือนปลายทาง — เด่นชัดกันลงผิดเดือน */}
          <div className="rounded-xl bg-gradient-to-r from-pink/10 to-peach/20 border border-pink/30 px-4 py-3 flex items-center gap-3">
            <CalendarDays className="w-6 h-6 text-pink shrink-0" />
            <div className="leading-tight">
              <div className="text-xs text-gray-500">กำลังจะลงตารางเวรของเดือน</div>
              <div className="text-lg font-bold text-gray-800">{thaiMonthLabel(period)}</div>
            </div>
          </div>
          <div className="flex flex-wrap items-end gap-3">
            <div>
              <label className="label">เปลี่ยนเดือน</label>
              <input type="month" value={period} onChange={(e) => { setPeriod(e.target.value); setPreview(null); }} className="input" />
            </div>
            <div>
              <label className="label">เฉพาะแผนก (ทำเป็นกลุ่ม)</label>
              <select value={dept} onChange={(e) => setDept(e.target.value)} className="input">
                <option value="">ทุกแผนก</option>
                {(orgDepts ?? []).map((d) => <option key={d.id} value={d.name}>{d.name}</option>)}
              </select>
            </div>
            <button
              onClick={() => apiDownload(`/hr/shift/roster/export?month=${period}${dept ? `&dept=${encodeURIComponent(dept)}` : ''}`, `roster-${period}${dept ? `-${dept}` : ''}.csv`).catch(() => toast.error('ดาวน์โหลดไม่สำเร็จ'))}
              className="btn-secondary inline-flex items-center gap-1.5"
            >
              <Download className="w-4 h-4" /> ส่งออกเป็น Excel (แก้แล้วนำเข้ากลับ)
            </button>
            <button
              onClick={() => copyPrev.mutate({ period, overwrite: false }, {
                onSuccess: (r) => toast.success(`ดึงตารางเดือน ${r.from} มาแล้ว ${r.rows} ช่อง — กดส่งออกเพื่อแก้`),
                onError: (e) => toast.error(e instanceof Error ? e.message : 'ดึงไม่สำเร็จ'),
              })}
              disabled={copyPrev.isPending}
              className="btn-secondary inline-flex items-center gap-1.5"
            >
              <CopyPlus className="w-4 h-4" /> {copyPrev.isPending ? 'กำลังดึง…' : 'ดึงตารางเดือนก่อน'}
            </button>
          </div>

          {/* คำแนะนำ: พิมพ์อะไรในช่องได้บ้าง */}
          <div className="rounded-lg bg-teal-50 border border-teal-100 p-3 text-xs text-gray-700">
            <div className="font-medium text-teal-800 mb-1">พิมพ์ในช่องวันที่ได้แบบนี้:</div>
            <ul className="space-y-0.5">
              <li>• <b>ชื่อกะ</b> = วันนั้นเข้ากะนั้น — กะที่ตั้งไว้ตอนนี้:
                {shiftOpts.length ? ' ' : ' (ยังไม่มีกะ — ไปตั้งที่ “กะการทำงาน” ก่อน)'}
                {shiftOpts.map((s, i) => (
                  <span key={s.id} className="font-mono">{i > 0 ? ' · ' : ''}{s.name}</span>
                ))}
              </li>
              <li>• <b>หยุด</b> (หรือ off / ว / x / -) = วันหยุด</li>
              <li>• <b>ลา / พักร้อน</b> = วันลาที่อนุมัติแล้ว (ระบบเติมมาให้ในไฟล์ — อย่าลงกะทับ ถ้าปล่อยไว้ระบบจะข้ามให้เอง)</li>
              <li>• <b>เว้นว่าง</b> = ไม่กำหนด (ใช้กะประจำของคนนั้น ไม่ถูกแตะ)</li>
            </ul>
            <div className="mt-1.5 text-gray-500">
              อยากให้พิมพ์สั้นๆ (เช้า/บ่าย/เย็น/ดึก)? ตั้งชื่อกะให้สั้นแบบนั้นได้เลย หรือใช้ “ตั้งรหัสย่อ” ผูกคำที่หัวหน้าชอบเขียน → กะจริง
            </div>
          </div>
        </div>

        {/* วาง / อัปไฟล์ */}
        <div className="card p-4 space-y-3">
          <div className="flex items-center justify-between">
            <label className="label mb-0">วางตารางจาก Excel (หรืออัปไฟล์)</label>
            <label className="btn-secondary inline-flex items-center gap-1.5 cursor-pointer">
              <FileSpreadsheet className="w-4 h-4" /> เลือกไฟล์
              <input type="file" accept=".xlsx,.xls,.csv,.tsv,.txt" className="hidden"
                onChange={(e) => { const f = e.target.files?.[0]; if (f) onFile(f); }} />
            </label>
          </div>
          {fileName && <p className="text-xs text-gray-500">ไฟล์: {fileName}</p>}
          <textarea
            value={text}
            onChange={(e) => { setText(e.target.value); setPreview(null); }}
            rows={8}
            placeholder={'ชื่อ\t1\t2\t3\t4\t5\t6\t7\nสมชาย\tเช้า\tเช้า\tหยุด\tเช้า\tเช้า\tบ่าย\tหยุด\nสมหญิง\tบ่าย\tหยุด\tบ่าย\tบ่าย\tดึก\tดึก\tหยุด'}
            className="input font-mono text-xs w-full"
          />
          {text && !parsed.error && (
            <p className="text-xs text-teal-700">อ่านได้ {parsed.rows.length} คน · {parsed.dayCount} วัน</p>
          )}
          {text && parsed.error && <p className="text-xs text-red-600">{parsed.error}</p>}
          <button onClick={runPreview} disabled={previewM.isPending || !text} className="btn-cta w-full justify-center flex items-center gap-2">
            <UploadCloud className="w-4 h-4" /> {previewM.isPending ? 'กำลังตรวจ…' : 'ตรวจก่อนนำเข้า'}
          </button>
        </div>

        {/* ผลตรวจ */}
        {preview && d && (
          <div className="card p-4 space-y-4">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-center text-sm">
              <Stat label="พนักงานจับคู่ได้" value={d.employees_matched} tone="ok" />
              <Stat label="วันทำงาน" value={d.work_cells} tone="ok" />
              <Stat label="วันหยุด" value={d.off_cells} tone="muted" />
              <Stat label="อ่านรหัสไม่ออก" value={d.unknown_cells} tone={d.unknown_cells ? 'warn' : 'ok'} />
            </div>

            {/* รหัสที่อ่านไม่ออก → ให้ผูก */}
            {d.unknown_codes.length > 0 && (
              <div className="rounded-lg border border-amber-200 bg-amber-50/50 p-3 space-y-2">
                <p className="text-sm font-medium text-amber-800 flex items-center gap-1.5">
                  <AlertTriangle className="w-4 h-4" /> รหัสที่ระบบยังไม่รู้จัก — ผูกให้ครั้งเดียว ใช้ซ้ำทุกเดือน
                </p>
                {d.unknown_codes.map((u) => (
                  <div key={u.raw} className="flex items-center gap-2 text-sm">
                    <code className="bg-white border rounded px-2 py-1 min-w-[80px]">{u.raw}</code>
                    <span className="text-gray-400 text-xs">×{u.count}</span>
                    <span className="text-gray-400">→</span>
                    <select
                      className="input flex-1 py-1"
                      value={aliasDraft[u.raw] ?? ''}
                      onChange={(e) => setAliasDraft((s) => ({ ...s, [u.raw]: e.target.value }))}
                    >
                      <option value="">— เลือก —</option>
                      <option value={OFF_TOKEN}>วันหยุด</option>
                      <option value={IGNORE_TOKEN}>ข้าม (ไม่นำเข้า)</option>
                      {shiftOpts.map((s) => <option key={s.id} value={s.code}>{s.name} ({s.code})</option>)}
                    </select>
                  </div>
                ))}
                <button onClick={saveAliasesAndRepreview} disabled={saveAlias.isPending} className="btn-secondary text-sm">
                  {saveAlias.isPending ? 'กำลังบันทึก…' : 'บันทึกการแปลรหัส แล้วตรวจใหม่'}
                </button>
              </div>
            )}

            {/* ชื่อที่จับคู่ไม่ได้ */}
            {d.unmatched_keys.length > 0 && (
              <div className="rounded-lg border border-red-200 bg-red-50/50 p-3 text-sm">
                <p className="font-medium text-red-700">จับคู่พนักงานไม่ได้ {d.unmatched_keys.length} รายการ (จะถูกข้าม):</p>
                <p className="text-xs text-red-600 mt-1">{d.unmatched_keys.slice(0, 12).join(' · ')}{d.unmatched_keys.length > 12 ? ' …' : ''}</p>
              </div>
            )}

            {/* เตือนกฎหมาย: สัปดาห์ไม่มีวันหยุด */}
            {d.week_warnings.length > 0 && (
              <div className="rounded-lg border border-amber-200 bg-amber-50/50 p-3 text-sm">
                <p className="font-medium text-amber-800 flex items-center gap-1.5">
                  <AlertTriangle className="w-4 h-4" /> มีพนักงานบางสัปดาห์ไม่มีวันหยุดเลย (กฎหมายกำหนดอย่างน้อย 1 วัน/สัปดาห์)
                </p>
                <p className="text-xs text-amber-700 mt-1">{d.week_warnings.map((w) => w.name || w.emp_code).slice(0, 12).join(' · ')}</p>
              </div>
            )}

            <div className="rounded-lg bg-pink/5 border border-pink/20 px-3 py-2 text-sm text-gray-700 flex items-center gap-2">
              <CalendarDays className="w-4 h-4 text-pink shrink-0" />
              ตารางนี้จะถูกบันทึกลงเดือน <b className="text-gray-900">{thaiMonthLabel(period)}</b> — ตรวจให้แน่ใจก่อนกดยืนยัน
            </div>
            <button onClick={runCommit} disabled={commitM.isPending || d.writable_entries === 0} className="btn-cta w-full justify-center flex items-center gap-2">
              <CheckCircle2 className="w-4 h-4" /> {commitM.isPending ? 'กำลังนำเข้า…' : `ยืนยันนำเข้าลงเดือน ${thaiMonthLabel(period)} — ${d.writable_entries} ช่อง`}
            </button>
          </div>
        )}

        <div className="text-center"><Link to="/hr/time#shifts" className="text-sm text-teal hover:underline">ไปหน้ากะการทำงาน →</Link></div>
      </div>
    </AdminLayout>
  );
}

function Stat({ label, value, tone }: { label: string; value: number; tone: 'ok' | 'warn' | 'muted' }) {
  const color = tone === 'warn' ? 'text-amber-700' : tone === 'muted' ? 'text-gray-600' : 'text-teal-700';
  return (
    <div className="rounded-lg border border-gray-100 bg-gray-50/50 py-2">
      <div className={`text-lg font-semibold ${color}`}>{value}</div>
      <div className="text-xs text-gray-500">{label}</div>
    </div>
  );
}
